import { useEffect, useMemo, useState, useRef } from "react";
import { Calendar, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { localizer, getMessages } from "../../../helpers";
import { useCalendarStore } from "../../../hooks";
import { MyWeek, SuccessMessage } from "../../ui";
import { ModalEditScheduleEvent, ModalAddScheduleEvent } from "./";
import { EventCards } from "./EventCards";

const DnDCalendar = withDragAndDrop(Calendar);

export const ProcedureCalendar = () => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    calendar,
    message,

    // Methods
    startUpdateByProcedureDates,
  } = useCalendarStore();

  const { eventSchedules } = calendar;
  const calendarContainerRef = useRef(null);
  // States ------------------------------------------------//
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(undefined);
  const [editEvent, setEditeEvent] = useState(undefined);
  const [addevent, setAddEvent] = useState(undefined);
  const [currentView, setCurrentView] = useState(Views.WEEK);
  const [currentDate, setCurrentDate] = useState(new Date());
  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const { views } = useMemo(
    () => ({
      views: {
        month: true,
        week: MyWeek,
        day: true,
      },
    }),
    []
  );

  // Formulario --------------------------------------------//
  // Funciones ---------------------------------------------//
  const moveEvent = async (event) => {
    const { event: eventBefore, start, end } = event;
    startUpdateByProcedureDates(eventBefore.id, start, end);
  };

  const handleSelectSlot = async (event) => {
    if (event.action === "select") {
      setSelectedEvent(event);
      setAddEvent(true);
    }
  };

  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    setEditeEvent(true);
  };

  const eventPropGetter = (event) => {
    return {
      className:
        event.isPastEvent && event.patientSurgery
          ? "bg-secondary"
          : event.isPastEvent
          ? "bg-gray-500"
          : event.patientSurgery
          ? "bg-primary"
          : event.state === "FREE"
          ? "bg-success"
          : "bg-primary",
      style: {
        borderRadius: "5px",
        border: "none",
      },
    };
  };

  // Efectos -----------------------------------------------//
  useEffect(() => {
    if (eventSchedules.length > 0) {
      const newEventSchedules = eventSchedules.map((eventSchedule) => ({
        ...eventSchedule,
        end: new Date(eventSchedule.end),
        start: new Date(eventSchedule.start),
      }));
      return setEvents(newEventSchedules);
    }
    return setEvents([]);
  }, [eventSchedules]);

  useEffect(() => {
    if (selectedEvent) {
      const updatedEvent = eventSchedules.find(
        (eventSchedule) => eventSchedule.id === selectedEvent.id
      );

      if (updatedEvent) {
        setSelectedEvent(updatedEvent);
      }
    }
  }, [eventSchedules, selectedEvent]);
  //Scroll al inicio
  useEffect(() => {
    if (!events || events.length === 0) return;

    setTimeout(() => {
      if (!calendarContainerRef.current) return;

      if (window.innerWidth > 768) return;

      const firstEventEl =
        calendarContainerRef.current.querySelector(".rbc-event");
      if (firstEventEl) {
        firstEventEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
    //Habilitar eventos
  }, [events, currentView, currentDate]);

  return (
    <>
      <div className="card border-0 rounded shadow mb-4">
        <div className="card-body">
          <div className="row justify-content-center g-3">
            <div className="col-md-4 col-lg-3">
              <div className="d-flex align-items-center">
                <div
                  className="bg-success rounded-circle"
                  style={{ width: "1.5rem", height: "1.5rem" }}
                ></div>
                <div className="ms-2">Sesiones disponibles</div>
              </div>
            </div>
            <div className="col-md-4 col-lg-3">
              <div className="d-flex align-items-center">
                <div
                  className="bg-primary rounded-circle"
                  style={{ width: "1.5rem", height: "1.5rem" }}
                ></div>
                <div className="ms-2">Sesiones agendadas</div>
              </div>
            </div>
            <div className="col-md-4 col-lg-3">
              <div className="d-flex align-items-center">
                <div
                  className="bg-gray-500 rounded-circle"
                  style={{ width: "1.5rem", height: "1.5rem" }}
                ></div>
                <div className="ms-2">Sesiones pasadas</div>
              </div>
            </div>
            <div className="col-md-4 col-lg-3">
              <div className="d-flex align-items-center">
                <div
                  className="bg-secondary rounded-circle"
                  style={{ width: "1.5rem", height: "1.5rem" }}
                ></div>
                <div className="ms-2">Sesiones pasadas agendadas</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {message?.type === "success" && (
        <SuccessMessage className="mb-4">{message.text}</SuccessMessage>
      )}

      <div className="row">
        <div className="col-md-12 ">
          <div className="card border-0 rounded shadow">
            <div className="card-header h6">{calendar.title}</div>
            <div className="card-body overflow-auto" ref={calendarContainerRef}>
              <DnDCalendar
                dayLayoutAlgorithm="no-overlap"
                culture="es"
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{
                  height: isMobile
                    ? "100vh"
                    : "clamp(500px, calc(100vh - 200px), 600px)",
                }}
                messages={getMessages()}
                defaultView={Views.WEEK}
                views={views}
                onEventDrop={moveEvent}
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                onNavigate={(date) => setCurrentDate(date)}
                onView={(view) => setCurrentView(view)}
                scrollToTime={new Date(new Date().setHours(7, 0, 0))}
                step={isMobile ? 10 : 10}
                // timeslots={isMobile ? 2 : 1}
                eventPropGetter={eventPropGetter}
                components={{
                  event: EventCards,
                }}
                min={new Date(1970, 0, 1, 6, 0)}
                max={new Date(1970, 0, 1, 23, 59)}
              />
            </div>
          </div>
        </div>
      </div>

      <ModalEditScheduleEvent
        event={selectedEvent}
        isOpen={editEvent}
        setIsOpen={setEditeEvent}
        isProcedure={true}
      />

      <ModalAddScheduleEvent
        event={selectedEvent}
        addevent={addevent}
        setAddEvent={setAddEvent}
      />
    </>
  );
};
